import { useContext, useEffect, useId } from "react";

import { OrgEventStreamContext } from "@/contexts/OrgEventStreamContext";
import type { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

export type EventInterest = {
    resourceType: ResourceType;
    resourceIds?: string[];
    creatorIds?: string[];
};

/**
 * Registers an interest in a specific resource type on the org-scoped event stream.
 * When the scoped-events feature flag is enabled, the manager merges all active
 * interests into ResourceTypeFilter messages sent to WatchEvents.
 *
 * Call this from query hooks that need real-time updates for their data.
 * The interest is automatically removed when the calling component unmounts.
 */
export const useEventInterest = (interest: EventInterest) => {
    const manager = useContext(OrgEventStreamContext);
    const id = useId();

    useEffect(() => {
        if (!manager) {
            return;
        }

        manager.addInterest(id, interest);
        return () => {
            manager.removeInterest(id);
        };
        // Serialize interest fields to avoid re-registering on every render.
        // The resourceType is a number, and the ID arrays are small and stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager, id, interest.resourceType, interest.resourceIds?.join(","), interest.creatorIds?.join(",")]);
};
